import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Mail, Phone, Calendar, MapPin } from "lucide-react";
import { PageShell } from "@/components/Navbar";
import { PageBanner, PAGE_BANNERS } from "@/components/PageBanner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const TYPE_LABEL = { GENERAL: "General", INFORMATION: "Information", PRICE_PAYMENT: "Price & payment", VIEWING_REQUEST: "Viewing request", AVAILABILITY_REQUEST: "Availability request", PROFILE_INQUIRY: "Profile inquiry" };
const STATUSES = ["NEW", "CONTACTED", "CLOSED"];
const STATUS_STYLE = { NEW: "bg-emerald-50 text-emerald-600", CONTACTED: "bg-amber-50 text-amber-600", CLOSED: "bg-slate-100 text-slate-500" };

export default function Inquiries() {
  const [items, setItems] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/me/listing-inquiries"); setItems(data); } catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/me/listing-inquiries/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <PageShell testid="inquiries-page">
      <PageBanner
        testid="inquiries-banner"
        image={PAGE_BANNERS.inquiries}
        title="Inquiries"
        description="Leads, viewing requests and private profile inquiries sent to you."
      />

      {items === null ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : items.length === 0 ? (
        <div data-testid="inquiries-empty" className="bg-white border border-dashed border-slate-300 rounded-2xl p-14 text-center">
          <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3"><Inbox className="h-6 w-6 text-slate-400" /></div>
          <p className="text-slate-600 font-medium">No inquiries yet</p>
          <p className="text-sm text-slate-400 mt-1">When someone inquires on your public listings, it will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((i) => (
            <div key={i.id} data-testid={`inquiry-row-${i.id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#0F2C59] text-white">{TYPE_LABEL[i.inquiry_type] || i.inquiry_type}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[i.status]}`} data-testid={`inquiry-status-${i.id}`}>{i.status}</span>
                  </div>
                  {i.inquiry_type === "PROFILE_INQUIRY" ? (
                    <div className="mt-2" data-testid={`inquiry-profile-${i.id}`}>
                      <p className="text-sm font-semibold text-slate-900">{i.subject}</p>
                      <p className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        {i.preferred_location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{i.preferred_location}</span>}
                        {i.attribution_label && <span data-testid={`inquiry-attribution-${i.id}`} className="font-semibold text-[#A9802B]">{i.attribution_label}</span>}
                      </p>
                    </div>
                  ) : (<>
                  <Link to={`/listing/${i.listing_id}`} className="block text-sm font-semibold text-slate-900 mt-2 hover:text-[#0F2C59]">{i.listing_title || "Listing"}</Link>
                  <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" /> {i.listing_city || "—"}</p>
                  </>)}
                </div>
                <span className="text-xs text-slate-400">{(i.created_at || "").slice(0, 10)}</span>
              </div>

              {i.inquiry_type === "VIEWING_REQUEST" && i.preferred_date && (
                <p className="text-sm text-slate-700 mt-3 flex items-center gap-1.5"><Calendar className="h-4 w-4 text-[#C79A3B]" /> Preferred: <span className="font-semibold">{i.preferred_date}{i.preferred_time ? ` · ${i.preferred_time}` : ""}</span></p>
              )}
              {i.inquiry_type === "AVAILABILITY_REQUEST" && i.check_in && (
                <p data-testid={`availability-detail-${i.id}`} className="text-sm text-slate-700 mt-3 flex items-center gap-1.5"><Calendar className="h-4 w-4 text-emerald-600" /> Stay: <span className="font-semibold">{i.check_in} → {i.check_out}{i.guests ? ` · ${i.guests} guest(s)` : ""}</span></p>
              )}
              {i.message && <p className="text-sm text-slate-600 mt-3 bg-slate-50 rounded-lg p-3 whitespace-pre-line">{i.message}</p>}

              <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-slate-100">
                <span className="text-sm text-slate-700 font-medium">{i.contact_name}{!i.is_registered_consumer && <span className="text-xs text-slate-400 font-normal"> (guest)</span>}</span>
                <a href={`mailto:${i.contact_email}`} data-testid={`inquiry-email-${i.id}`} className="text-sm text-[#0F2C59] flex items-center gap-1 hover:underline"><Mail className="h-3.5 w-3.5" /> {i.contact_email}</a>
                {i.contact_mobile && <a href={`tel:${i.contact_mobile}`} className="text-sm text-slate-600 flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {i.contact_mobile}</a>}
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-slate-400">Status</span>
                  <Select value={i.status} onValueChange={(v) => setStatus(i.id, v)}>
                    <SelectTrigger data-testid={`inquiry-status-select-${i.id}`} className="h-9 w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </PageShell>
  );
}
